import {
  DataTableDataProvider,
  DataTableDataRequest,
  DataTableFilterDescriptor,
  DataTableSortDescriptor,
} from './data-table-provider';

export interface DataTableControllerCriteria {
  sort: DataTableSortDescriptor[];
  filters: DataTableFilterDescriptor[];
  freeText?: string;
  requiresCount?: boolean;
}

export interface DataTableControllerState<TItem = unknown> {
  items: TItem[];
  loading: boolean;
  error?: unknown;
  hasMore: boolean;
  totalCount?: number;
  sliceStart: number;
  sliceLength: number;
}

export type DataTableControllerListener<TItem> = (
  state: DataTableControllerState<TItem>
) => void;

export interface DataTableControllerOptions<TItem = unknown> {
  provider?: DataTableDataProvider<TItem>;
  sliceLength?: number;
}

export interface DataTableLoadOptions {
  sliceStart?: number;
  sliceLength?: number;
  requiresCount?: boolean;
}

export class DataTableController<TItem = unknown> {
  private provider?: DataTableDataProvider<TItem>;
  private criteria: DataTableControllerCriteria = { sort: [], filters: [] };
  private sliceLength: number;
  private listeners = new Set<DataTableControllerListener<TItem>>();
  private state: DataTableControllerState<TItem>;
  private requestToken = 0;
  private items: TItem[] = [];

  constructor(options?: DataTableControllerOptions<TItem>) {
    this.provider = options?.provider;
    this.sliceLength = Math.max(1, options?.sliceLength ?? 50);
    this.state = {
      items: [],
      loading: false,
      hasMore: false,
      sliceStart: 0,
      sliceLength: this.sliceLength,
    };
  }

  setProvider(provider: DataTableDataProvider<TItem>) {
    this.provider = provider;
  }

  setSliceLength(length: number) {
    this.sliceLength = Math.max(1, length);
    this.updateState({ sliceLength: this.sliceLength });
  }

  getState(): DataTableControllerState<TItem> {
    return this.state;
  }

  subscribe(listener: DataTableControllerListener<TItem>): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  async loadInitial(
    criteria: DataTableControllerCriteria,
    options?: DataTableLoadOptions
  ): Promise<DataTableControllerState<TItem>> {
    this.criteria = criteria;
    if (options?.sliceLength) this.setSliceLength(options.sliceLength);
    return this.fetchSlice({
      sliceStart: options?.sliceStart ?? 0,
      requiresCount: options?.requiresCount ?? criteria.requiresCount ?? false,
      replace: true,
    });
  }

  async refresh(options?: DataTableLoadOptions) {
    return this.fetchSlice({
      sliceStart: options?.sliceStart ?? 0,
      requiresCount:
        options?.requiresCount ?? this.criteria.requiresCount ?? false,
      replace: true,
    });
  }

  async loadMore(options?: DataTableLoadOptions) {
    if (!this.state.hasMore) return this.state;
    return this.fetchSlice({
      sliceStart: options?.sliceStart ?? this.items.length,
      requiresCount: options?.requiresCount ?? false,
      replace: false,
    });
  }

  private async fetchSlice(config: {
    sliceStart: number;
    requiresCount: boolean;
    replace: boolean;
  }): Promise<DataTableControllerState<TItem>> {
    if (!this.provider)
      throw new Error('DataTableController requires a data provider.');

    const request: DataTableDataRequest = {
      sort: this.criteria.sort,
      filters: this.criteria.filters,
      freeText: this.criteria.freeText,
      requiresCount: config.requiresCount,
      sliceStart: config.sliceStart,
      sliceLength: this.sliceLength,
    };

    const token = ++this.requestToken;
    this.updateState({
      loading: true,
      error: undefined,
      sliceStart: config.sliceStart,
    });

    try {
      const response = await this.provider(request);
      if (token !== this.requestToken) return this.state;

      this.items = config.replace
        ? [...response.items]
        : [...this.items, ...response.items];

      this.updateState({
        loading: false,
        items: [...this.items],
        hasMore: response.hasMore,
        totalCount:
          response.totalCount !== undefined
            ? response.totalCount
            : this.state.totalCount,
      });
    } catch (error) {
      if (token === this.requestToken) {
        this.updateState({ loading: false, error });
      }
      throw error;
    }

    return this.state;
  }

  private updateState(patch: Partial<DataTableControllerState<TItem>>) {
    this.state = {
      ...this.state,
      ...patch,
      items: patch.items ?? this.state.items,
      sliceLength: this.sliceLength,
    };
    this.notify();
  }

  private notify() {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}
